/*
 * petngo-pdp.js
 * ---------------------------------------------------------------------------
 * Vanilla, dependency-free behaviour for `sections/petngo-product-v3.liquid`
 * (the bespoke PDP). Liquid port of the Hydrogen PDP interactions:
 *
 *   1. Gallery — clicking a thumbnail swaps the main image (no nav). Variant
 *      selection also swaps the main image to the variant image.
 *   2. Variant selector — clicking an option pill resolves the matching
 *      variant from the embedded JSON, then updates the hidden id input,
 *      price, compare-at, availability, and main image. Mirrors the
 *      Hydrogen <VariantOptions> behaviour without a route navigation.
 *   3. Qty stepper — clamped [min,max], immutable (always sets a fresh value).
 *   4. Recompra toggle — reveals the frequency chip row and writes the chosen
 *      selling_plan id into the (enabled) hidden input. Only present when the
 *      product exposes selling plans.
 *   5. Sticky ATC bar — revealed on scroll once the main buy panel scrolls
 *      out of view; its button submits the same form.
 *   6. Add to cart — submits via fetch() to /cart/add.js then opens + refreshes
 *      the native Minimog cart drawer by dispatching the same `cart:refresh`
 *      event the theme's drawer listens for (identical mechanism to
 *      petngo-product-card.js). Errors surface inline; no silent failures.
 *
 * Idempotent: guarded on window.__pnPdpInit and a per-section bound flag so
 * Theme Editor section reloads don't double-bind.
 */
(function () {
  'use strict';

  if (window.__pnPdpInit) return;
  window.__pnPdpInit = true;

  var BOUND = 'data-pn-pdp-bound';
  var ADDED_FEEDBACK_MS = 1800;
  var ERROR_FEEDBACK_MS = 2600;

  var STRINGS = {
    adding: 'Agregando…',
    added: 'Agregado',
    error: 'Reintentar',
  };

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  function parseVariants(root) {
    var script = root.querySelector('[data-petngo-pdp-variants-json]');
    if (!script) return [];
    try {
      var parsed = JSON.parse(script.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      if (window.console && console.error) {
        console.error('[petngo-pdp] could not parse variant JSON:', e);
      }
      return [];
    }
  }

  /** Emit a Minimog pub/sub event if the bus is present (cart-count bubble). */
  function emitMinimog(eventName, payload) {
    try {
      if (
        window.MinimogEvents &&
        typeof window.MinimogEvents.emit === 'function' &&
        window.MinimogTheme &&
        window.MinimogTheme.pubSubEvents &&
        window.MinimogTheme.pubSubEvents[eventName]
      ) {
        window.MinimogEvents.emit(
          window.MinimogTheme.pubSubEvents[eventName],
          payload
        );
      }
    } catch (e) {
      /* non-fatal — drawer refresh still happens via cart:refresh */
    }
  }

  /**
   * Open + refresh the native Minimog cart drawer (same hook the product
   * card uses). Falls back to navigating to /cart when no drawer exists.
   */
  function openCartDrawer(cartData) {
    emitMinimog('cartUpdate', { cart: cartData });
    var drawer = document.getElementById('MinimogCartDrawer');
    if (drawer) {
      document.dispatchEvent(
        new CustomEvent('cart:refresh', { detail: { open: true } })
      );
      document.dispatchEvent(
        new CustomEvent('product-ajax:added', { detail: { product: cartData } })
      );
      return true;
    }
    return false;
  }

  function cartAddUrl() {
    return window.Shopify && window.Shopify.routes
      ? window.Shopify.routes.root + 'cart/add.js'
      : '/cart/add.js';
  }

  function cartUrl() {
    return window.Shopify && window.Shopify.routes
      ? window.Shopify.routes.root + 'cart'
      : '/cart';
  }

  // -------------------------------------------------------------------------
  // Gallery
  // -------------------------------------------------------------------------

  function setMainImage(root, url) {
    if (!url) return;
    // Target by class: Shopify's image_tag filter can't take a hyphenated
    // data-* named argument (Liquid parses the dashes as subtraction), so the
    // main image carries a stable class we select on here (same approach as
    // petngo-product-card.js).
    // Target the main image by its container (the image_tag `class:` arg
    // doesn't always render onto the <img>, leaving className empty — so the
    // old `.petngo-pdp-v3__main-img` selector matched nothing and the swap
    // silently no-op'd; Diego QA "el selector de imágenes no jala"). The
    // [data-petngo-pdp-main] wrapper is always present.
    var img = root.querySelector('[data-petngo-pdp-main] img') ||
              root.querySelector('.petngo-pdp-v3__main-img');
    if (!img) return;
    if (img.hasAttribute('srcset')) img.removeAttribute('srcset');
    img.src = url;
  }

  function bindGallery(root) {
    var thumbs = Array.prototype.slice.call(
      root.querySelectorAll('[data-petngo-pdp-thumb]')
    );
    if (thumbs.length === 0) return;

    function selectThumb(index) {
      thumbs.forEach(function (thumb, i) {
        var isActive = i === index;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      var full = thumbs[index].getAttribute('data-full');
      setMainImage(root, full);
    }

    thumbs.forEach(function (thumb, index) {
      thumb.addEventListener('click', function () {
        selectThumb(index);
      });
    });
  }

  /** Highlight the thumb whose full image matches the variant image. */
  function syncThumbToImage(root, imageUrl) {
    if (!imageUrl) return;
    var thumbs = Array.prototype.slice.call(
      root.querySelectorAll('[data-petngo-pdp-thumb]')
    );
    if (thumbs.length === 0) return;
    // Compare on the path (ignoring width query) so the resized thumb URL
    // and the resized main URL still match.
    var target = imageUrl.split('?')[0];
    thumbs.forEach(function (thumb) {
      var full = (thumb.getAttribute('data-full') || '').split('?')[0];
      var isMatch = full === target;
      if (isMatch) {
        thumb.classList.add('is-active');
        thumb.setAttribute('aria-selected', 'true');
      } else {
        thumb.classList.remove('is-active');
        thumb.setAttribute('aria-selected', 'false');
      }
    });
  }

  // -------------------------------------------------------------------------
  // Variant selection
  // -------------------------------------------------------------------------

  function findVariant(variants, selected) {
    for (var i = 0; i < variants.length; i++) {
      var opts = variants[i].options || [];
      var match = true;
      for (var j = 0; j < selected.length; j++) {
        if (selected[j] != null && opts[j] !== selected[j]) {
          match = false;
          break;
        }
      }
      if (match) return variants[i];
    }
    return null;
  }

  function bindVariants(root) {
    var variants = parseVariants(root);
    var groups = Array.prototype.slice.call(
      root.querySelectorAll('[data-petngo-pdp-variants] [data-option-index]')
    );
    var pills = Array.prototype.slice.call(
      root.querySelectorAll('[data-petngo-pdp-option]')
    );
    if (variants.length === 0 || pills.length === 0) return;

    var idInput = root.querySelector('[data-petngo-pdp-variant-input]');
    var priceEl = root.querySelector('[data-petngo-pdp-price]');
    var compareEl = root.querySelector('[data-petngo-pdp-compare]');
    var stickyPriceEl = root.querySelector('[data-petngo-pdp-sticky-price]');
    var atcBtn = root.querySelector('[data-petngo-pdp-atc]');
    var atcLabel = root.querySelector('[data-petngo-pdp-atc-label]');
    var stickyBtn = root.querySelector('[data-petngo-pdp-sticky-atc]');

    // Build the current selection array indexed by option position.
    function currentSelection() {
      var selection = [];
      pills.forEach(function (pill) {
        if (pill.getAttribute('aria-checked') === 'true') {
          var idx = parseInt(pill.getAttribute('data-option-index'), 10);
          selection[idx] = pill.getAttribute('data-option-value');
        }
      });
      return selection;
    }

    function setAtcAvailability(available) {
      [atcBtn, stickyBtn].forEach(function (btn) {
        if (!btn) return;
        if (available) {
          btn.removeAttribute('disabled');
          btn.removeAttribute('aria-disabled');
        } else {
          btn.setAttribute('disabled', 'disabled');
          btn.setAttribute('aria-disabled', 'true');
        }
      });
    }

    function applyVariant(variant) {
      if (!variant) {
        setAtcAvailability(false);
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (priceEl && variant.price_formatted) {
        priceEl.textContent = variant.price_formatted;
      }
      if (stickyPriceEl && variant.price_formatted) {
        stickyPriceEl.textContent = variant.price_formatted;
      }
      if (compareEl) {
        if (variant.compare_at && variant.compare_at > variant.price) {
          compareEl.textContent = variant.compare_at_formatted;
          compareEl.removeAttribute('hidden');
        } else {
          compareEl.textContent = '';
          compareEl.setAttribute('hidden', 'hidden');
        }
      }
      if (variant.image) {
        setMainImage(root, variant.image);
        syncThumbToImage(root, variant.image);
      }
      setAtcAvailability(Boolean(variant.available));
    }

    function selectPill(pill) {
      var idx = parseInt(pill.getAttribute('data-option-index'), 10);
      // Deselect siblings in the same option group, select this one.
      pills.forEach(function (other) {
        if (parseInt(other.getAttribute('data-option-index'), 10) === idx) {
          var isThis = other === pill;
          other.classList.toggle('is-active', isThis);
          other.setAttribute('aria-checked', isThis ? 'true' : 'false');
        }
      });
      var variant = findVariant(variants, currentSelection());
      applyVariant(variant);
    }

    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        selectPill(pill);
      });
      pill.addEventListener('keydown', function (e) {
        var idx = parseInt(pill.getAttribute('data-option-index'), 10);
        var siblings = pills.filter(function (o) {
          return parseInt(o.getAttribute('data-option-index'), 10) === idx;
        });
        var pos = siblings.indexOf(pill);
        var next = pos;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          next = (pos + 1) % siblings.length;
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          next = (pos - 1 + siblings.length) % siblings.length;
        } else {
          return;
        }
        e.preventDefault();
        selectPill(siblings[next]);
        siblings[next].focus();
      });
    });
  }

  // -------------------------------------------------------------------------
  // Qty stepper
  // -------------------------------------------------------------------------

  function bindQty(root) {
    var input = root.querySelector('[data-petngo-pdp-qty-input]');
    var dec = root.querySelector('[data-petngo-pdp-qty-dec]');
    var inc = root.querySelector('[data-petngo-pdp-qty-inc]');
    if (!input) return;

    var min = parseInt(input.getAttribute('min'), 10) || 1;
    var max = parseInt(input.getAttribute('max'), 10) || 99;

    function clamp(n) {
      if (isNaN(n)) return min;
      if (n < min) return min;
      if (n > max) return max;
      return n;
    }
    function setValue(n) {
      input.value = String(clamp(n));
    }
    if (dec) {
      dec.addEventListener('click', function () {
        setValue(parseInt(input.value, 10) - 1);
      });
    }
    if (inc) {
      inc.addEventListener('click', function () {
        setValue(parseInt(input.value, 10) + 1);
      });
    }
    input.addEventListener('change', function () {
      setValue(parseInt(input.value, 10));
    });
  }

  // -------------------------------------------------------------------------
  // Recompra (selling plans)
  // -------------------------------------------------------------------------

  function bindRecompra(root) {
    var wrap = root.querySelector('[data-petngo-pdp-recompra]');
    if (!wrap) return;
    var check = wrap.querySelector('[data-petngo-pdp-recompra-check]');
    var plansRow = wrap.querySelector('#petngo-pdp-recompra-plans');
    var planButtons = Array.prototype.slice.call(
      wrap.querySelectorAll('[data-petngo-pdp-plan]')
    );
    var planInput = wrap.querySelector('[data-petngo-pdp-plan-input]');
    if (!check || !planInput) return;

    function activePlanId() {
      for (var i = 0; i < planButtons.length; i++) {
        if (planButtons[i].getAttribute('aria-checked') === 'true') {
          return planButtons[i].getAttribute('data-plan-id');
        }
      }
      return planButtons.length ? planButtons[0].getAttribute('data-plan-id') : '';
    }

    function syncPlanInput() {
      if (check.checked) {
        planInput.value = activePlanId();
        planInput.removeAttribute('disabled');
      } else {
        planInput.value = '';
        planInput.setAttribute('disabled', 'disabled');
      }
    }

    check.addEventListener('change', function () {
      if (plansRow) {
        if (check.checked) plansRow.removeAttribute('hidden');
        else plansRow.setAttribute('hidden', 'hidden');
      }
      syncPlanInput();
    });

    planButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        planButtons.forEach(function (other) {
          var isThis = other === btn;
          other.classList.toggle('pn-recompra-plan--active', isThis);
          other.setAttribute('aria-checked', isThis ? 'true' : 'false');
        });
        syncPlanInput();
      });
    });
  }

  // -------------------------------------------------------------------------
  // Share button — native Web Share when available, clipboard copy fallback.
  // Mirrors the Hydrogen secondary-actions share affordance. Never throws;
  // a user-cancelled share or an unsupported environment both fail quietly
  // for the user while logging detail for the developer.
  // -------------------------------------------------------------------------

  function bindShare(root) {
    var btn = root.querySelector('[data-petngo-pdp-share]');
    if (!btn) return;
    var label = btn.querySelector('[data-petngo-pdp-share-label]');
    var idleText = label ? label.textContent.trim() : '';
    var copiedText = btn.getAttribute('data-share-copied') || 'Enlace copiado';
    var shareTitle = btn.getAttribute('data-share-title') || document.title;
    var feedbackTimer = null;

    function flashCopied() {
      if (!label) return;
      if (feedbackTimer) clearTimeout(feedbackTimer);
      label.textContent = copiedText;
      feedbackTimer = setTimeout(function () {
        label.textContent = idleText;
      }, ADDED_FEEDBACK_MS);
    }

    function copyToClipboard(url) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(url)
          .then(flashCopied)
          .catch(function (err) {
            if (window.console && console.warn) {
              console.warn('[petngo-pdp] clipboard copy failed:', err);
            }
          });
        return;
      }
      // Legacy fallback: a transient textarea + execCommand('copy').
      try {
        var ta = document.createElement('textarea');
        ta.value = url;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        flashCopied();
      } catch (err) {
        if (window.console && console.warn) {
          console.warn('[petngo-pdp] legacy clipboard copy failed:', err);
        }
      }
    }

    btn.addEventListener('click', function () {
      var url = window.location.href;
      if (navigator.share) {
        navigator
          .share({ title: shareTitle, url: url })
          .catch(function (err) {
            // AbortError = user dismissed the share sheet; not an error.
            if (err && err.name === 'AbortError') return;
            copyToClipboard(url);
          });
        return;
      }
      copyToClipboard(url);
    });
  }

  // -------------------------------------------------------------------------
  // Sticky ATC reveal
  // -------------------------------------------------------------------------

  function bindSticky(root) {
    var sticky = root.querySelector('[data-petngo-pdp-sticky]');
    var anchor = root.querySelector('.pn-pdp-card');
    if (!sticky || !anchor) return;

    var anchorVisible = true;
    function update() {
      var scrolledEnough = window.scrollY > window.innerHeight * 0.5;
      var show = scrolledEnough && !anchorVisible;
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
    }

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          anchorVisible = entries[0] ? entries[0].isIntersecting : false;
          update();
        },
        { rootMargin: '0px 0px -144px 0px', threshold: 0.12 }
      );
      observer.observe(anchor);
    }
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
  }

  // -------------------------------------------------------------------------
  // Add to cart
  // -------------------------------------------------------------------------

  function bindForm(root) {
    var form = root.querySelector('[data-petngo-pdp-form]');
    if (!form) return;

    var primaryBtn = root.querySelector('[data-petngo-pdp-atc]');
    var primaryLabel = root.querySelector('[data-petngo-pdp-atc-label]');
    var stickyBtn = root.querySelector('[data-petngo-pdp-sticky-atc]');
    var stickyLabel = root.querySelector('[data-petngo-pdp-sticky-label]');
    var live = root.querySelector('[data-petngo-pdp-live]');
    var title = form.getAttribute('data-product-title') || '';
    var idlePrimary = primaryLabel ? primaryLabel.textContent.trim() : '';
    var idleSticky = stickyLabel ? stickyLabel.textContent.trim() : '';
    var resetTimer = null;

    function setLabels(text) {
      if (primaryLabel) primaryLabel.textContent = text;
      if (stickyLabel) stickyLabel.textContent = text;
    }
    function setDisabled(disabled) {
      [primaryBtn, stickyBtn].forEach(function (btn) {
        if (!btn) return;
        if (disabled) btn.setAttribute('disabled', 'disabled');
        else btn.removeAttribute('disabled');
      });
    }
    function announce(text) {
      if (live) live.textContent = text;
    }
    function restoreIdle() {
      if (primaryLabel) primaryLabel.textContent = idlePrimary;
      if (stickyLabel) stickyLabel.textContent = idleSticky;
      setDisabled(false);
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (primaryBtn && primaryBtn.hasAttribute('disabled')) return;

      if (resetTimer) {
        clearTimeout(resetTimer);
        resetTimer = null;
      }
      setDisabled(true);
      setLabels(STRINGS.adding);

      var formData = new FormData(form);

      fetch(cartAddUrl(), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      })
        .then(function (res) {
          return res.json().then(function (data) {
            return { ok: res.ok, data: data };
          });
        })
        .then(function (result) {
          if (!result.ok || (result.data && result.data.status)) {
            throw new Error(
              (result.data && (result.data.description || result.data.message)) ||
                'cart_add_failed'
            );
          }
          setLabels(STRINGS.added + ' ✓');
          announce(title + ' — ' + STRINGS.added);

          var opened = openCartDrawer(result.data);
          if (!opened) {
            window.location.href = cartUrl();
            return;
          }
          resetTimer = setTimeout(restoreIdle, ADDED_FEEDBACK_MS);
        })
        .catch(function (err) {
          if (window.console && console.error) {
            console.error('[petngo-pdp] add to cart failed:', err);
          }
          setLabels(STRINGS.error);
          announce(STRINGS.error);
          resetTimer = setTimeout(restoreIdle, ERROR_FEEDBACK_MS);
        });
    });

    // ---- "Comprar ahora": add the selected variant + qty, then go straight to
    //      /checkout — SAME UX as the product cards. Replaces the dynamic
    //      `form | payment_button`, which sent users straight to PayPal (Diego). ----
    var buyNowBtn = root.querySelector('[data-petngo-pdp-buynow]');
    if (buyNowBtn) {
      var buyNowLabel = root.querySelector('[data-petngo-pdp-buynow-label]');
      var idleBuyNow = buyNowLabel ? buyNowLabel.textContent.trim() : '';
      var buyNowTimer = null;
      buyNowBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (buyNowBtn.hasAttribute('disabled')) return;
        if (primaryBtn && primaryBtn.hasAttribute('disabled')) return;
        if (buyNowTimer) {
          clearTimeout(buyNowTimer);
          buyNowTimer = null;
        }
        buyNowBtn.setAttribute('disabled', 'disabled');
        if (buyNowLabel) buyNowLabel.textContent = STRINGS.adding;

        fetch(cartAddUrl(), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: new FormData(form),
        })
          .then(function (res) {
            return res.json().then(function (data) {
              return { ok: res.ok, data: data };
            });
          })
          .then(function (result) {
            if (!result.ok || (result.data && result.data.status)) {
              throw new Error(
                (result.data && (result.data.description || result.data.message)) ||
                  'cart_add_failed'
              );
            }
            announce(title + ' — ' + STRINGS.added);
            window.location.href =
              window.Shopify && window.Shopify.routes
                ? window.Shopify.routes.root + 'checkout'
                : '/checkout';
          })
          .catch(function (err) {
            if (window.console && console.error) {
              console.error('[petngo-pdp] buy now failed:', err);
            }
            if (buyNowLabel) buyNowLabel.textContent = STRINGS.error;
            announce(STRINGS.error);
            buyNowTimer = setTimeout(function () {
              if (buyNowLabel) buyNowLabel.textContent = idleBuyNow;
              buyNowBtn.removeAttribute('disabled');
            }, ERROR_FEEDBACK_MS);
          });
      });
    }
  }

  // -------------------------------------------------------------------------
  // Cross-sell rail: lazily fetch Shopify recommendations when none were
  // rendered server-side (the recommendations object isn't always populated
  // on the first render). Mirrors the Hydrogen deferred related-products.
  // -------------------------------------------------------------------------

  function loadCrossSell(root) {
    var rail = root.querySelector('[data-petngo-pdp-crosssell]');
    if (!rail) return;
    var grid = rail.querySelector('[data-petngo-pdp-crosssell-grid]');
    var url = rail.getAttribute('data-reco-url');
    if (!grid || !url) return;
    // Already populated server-side (collection fallback or warm reco cache).
    if (grid.children.length > 0) return;

    fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) {
        return res.ok ? res.text() : Promise.reject(new Error('reco_failed'));
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-petngo-pdp-crosssell-grid]');
        if (fresh && fresh.children.length > 0) {
          grid.innerHTML = fresh.innerHTML;
          // Let the product-card script bind the freshly injected cards.
          document.dispatchEvent(new CustomEvent('cart:updated'));
        } else {
          rail.setAttribute('hidden', 'hidden');
        }
      })
      .catch(function () {
        rail.setAttribute('hidden', 'hidden');
      });
  }

  // -------------------------------------------------------------------------
  // -------------------------------------------------------------------------
  // Image zoom (desktop hover) + lightbox (click → full-screen)
  // El CSS (.pn-pdp-gallery--zoom / .pn-pdp-lightbox / __zoom-badge) ya vive
  // en petngo-components.css; este es el JS que faltaba (Diego QA: "el zoom no
  // jala"). Hover: mousemove → scale + transform-origin en el cursor. Click:
  // lightbox con close/prev/next, teclado (Esc/←/→), swipe y restore de foco.
  // -------------------------------------------------------------------------

  var ZOOM_SCALE = 1.8;

  function galleryUrls(root) {
    var thumbs = root.querySelectorAll('[data-petngo-pdp-thumb]');
    if (thumbs.length) {
      return Array.prototype.map
        .call(thumbs, function (t) { return t.getAttribute('data-full'); })
        .filter(Boolean);
    }
    var img = root.querySelector('[data-petngo-pdp-main] img');
    return img ? [img.currentSrc || img.src] : [];
  }

  function bindZoom(root) {
    var stage = root.querySelector('[data-petngo-pdp-main]');
    if (!stage) return;

    // Inject the zoom badge (lupa) once — CSS already styles it.
    if (!stage.querySelector('.pn-pdp-main-img__zoom-badge')) {
      var badge = document.createElement('span');
      badge.className = 'pn-pdp-main-img__zoom-badge';
      badge.setAttribute('aria-hidden', 'true');
      badge.innerHTML =
        '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"></circle><path d="M21 21l-4.3-4.3M11 8v6M8 11h6"></path></svg>';
      stage.appendChild(badge);
    }

    var canHover = window.matchMedia('(hover: hover) and (min-width: 768px)').matches;
    if (canHover) {
      stage.addEventListener('mousemove', function (e) {
        var current = stage.querySelector('img');
        if (!current) return;
        var r = stage.getBoundingClientRect();
        var x = ((e.clientX - r.left) / r.width) * 100;
        var y = ((e.clientY - r.top) / r.height) * 100;
        current.style.transformOrigin = x + '% ' + y + '%';
        current.style.transform = 'scale(' + ZOOM_SCALE + ')';
        stage.classList.add('is-zooming');
      });
      stage.addEventListener('mouseleave', function () {
        var current = stage.querySelector('img');
        if (current) current.style.transform = '';
        stage.classList.remove('is-zooming');
      });
    }

    // Click / keyboard → lightbox.
    stage.setAttribute('role', 'button');
    stage.setAttribute('tabindex', '0');
    stage.setAttribute('aria-label', 'Ampliar imagen');
    function openFromStage() {
      var current = stage.querySelector('img');
      openLightbox(root, current ? (current.currentSrc || current.src) : null);
    }
    stage.addEventListener('click', openFromStage);
    stage.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFromStage(); }
    });
  }

  function openLightbox(root, startSrc) {
    var urls = galleryUrls(root);
    if (!urls.length) return;
    var idx = 0;
    if (startSrc) {
      var base = startSrc.split('?')[0];
      for (var i = 0; i < urls.length; i++) {
        if ((urls[i] || '').split('?')[0] === base) { idx = i; break; }
      }
    }
    var multi = urls.length > 1;
    var prevFocus = document.activeElement;
    var chevron =
      '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 6 15 12 9 18"></polyline></svg>';

    var lb = document.createElement('div');
    lb.className = 'pn-pdp-lightbox';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', 'Imagen del producto');
    lb.tabIndex = -1;
    lb.innerHTML =
      '<button type="button" class="pn-pdp-lightbox__close" aria-label="Cerrar">' +
        '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 6l12 12M18 6L6 18"></path></svg>' +
      '</button>' +
      (multi
        ? '<button type="button" class="pn-pdp-lightbox__nav pn-pdp-lightbox__nav--prev" aria-label="Anterior">' + chevron + '</button>' +
          '<button type="button" class="pn-pdp-lightbox__nav pn-pdp-lightbox__nav--next" aria-label="Siguiente">' + chevron + '</button>'
        : '') +
      '<figure class="pn-pdp-lightbox__figure">' +
        '<img class="pn-pdp-lightbox__image" alt="" src="' + urls[idx] + '">' +
      '</figure>';

    var imgEl = lb.querySelector('.pn-pdp-lightbox__image');
    function show(n) { idx = (n + urls.length) % urls.length; imgEl.src = urls[idx]; }
    function close() {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      if (lb.parentNode) lb.parentNode.removeChild(lb);
      if (prevFocus && prevFocus.focus) prevFocus.focus();
    }
    function onKey(e) {
      if (e.key === 'Escape') close();
      else if (multi && e.key === 'ArrowLeft') show(idx - 1);
      else if (multi && e.key === 'ArrowRight') show(idx + 1);
    }

    lb.addEventListener('click', function (e) { if (e.target === lb) close(); });
    lb.querySelector('.pn-pdp-lightbox__close').addEventListener('click', close);
    if (multi) {
      lb.querySelector('.pn-pdp-lightbox__nav--prev').addEventListener('click', function (e) { e.stopPropagation(); show(idx - 1); });
      lb.querySelector('.pn-pdp-lightbox__nav--next').addEventListener('click', function (e) { e.stopPropagation(); show(idx + 1); });
      var sx = 0;
      lb.addEventListener('touchstart', function (e) { sx = e.touches[0].clientX; }, { passive: true });
      lb.addEventListener('touchend', function (e) {
        var dx = e.changedTouches[0].clientX - sx;
        if (Math.abs(dx) > 48) show(idx + (dx < 0 ? 1 : -1));
      }, { passive: true });
    }

    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    document.body.appendChild(lb);
    lb.focus();
  }

  // -------------------------------------------------------------------------
  // Boot
  // -------------------------------------------------------------------------

  function initPdp(root) {
    if (root.hasAttribute(BOUND)) return;
    root.setAttribute(BOUND, '');
    bindGallery(root);
    bindZoom(root);
    bindVariants(root);
    bindQty(root);
    bindRecompra(root);
    bindShare(root);
    bindSticky(root);
    bindForm(root);
    loadCrossSell(root);
  }

  function initAll(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var sections = root.querySelectorAll('[data-petngo-pdp]');
    for (var i = 0; i < sections.length; i++) initPdp(sections[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target || document);
  });
})();
